import { assert, expect } from 'chai';
import {
  Channel,
  Context,
  ContextMetadata,
  Listener,
  DesktopAgent,
  ApiEvent,
  FDC3ContextClearedEvent,
} from '@finos/fdc3';
import constants from '../../constants';
import { ChannelControl, ChannelsAppConfig, ChannelsAppContext } from '../support/channel-control';
import { MockAppTracker, waitForContext } from '../fdc3-conformance-utils';

export class ChannelControlImpl implements ChannelControl {
  private readonly testAppChannelName = 'test-channel';
  private readonly fdc3: DesktopAgent;
  private readonly mockAppTracker = new MockAppTracker();

  constructor(fdc3: DesktopAgent) {
    this.fdc3 = fdc3;
  }

  getNonGlobalUserChannels = async () => {
    const channels = await this.fdc3.getUserChannels();
    if (channels.find(channel => channel.id.indexOf('global') >= 0)) {
      assert.fail('Global channel received ');
    }
    return channels.filter(channel => channel.id.indexOf('global') === -1);
  };

  getNonGlobalUserChannel = async (): Promise<Channel> => {
    const channels = await this.getNonGlobalUserChannels();
    if (channels.length > 0) {
      return channels[0];
    } else {
      assert.fail('No system channels available for app A');
    }
  };

  leaveChannel = async () => {
    return await this.fdc3.leaveCurrentChannel();
  };

  joinChannel = async (channel: Channel) => {
    return this.fdc3.joinUserChannel(channel.id);
  };

  createRandomTestChannel = async () => {
    const channelName = `${this.testAppChannelName}.${this.getRandomId()}`;
    return await this.fdc3.getOrCreateChannel(channelName);
  };

  getCurrentChannel = async (): Promise<Channel | null> => {
    return await this.fdc3.getCurrentChannel();
  };

  unsubscribeListeners = async (listeners: Listener[]) => {
    listeners.map(listener => {
      listener.unsubscribe();
    });
  };

  initCompleteListener = async (testId: string) => {
    const { listenerPromise, listener } = await waitForContext(
      'executionComplete',
      testId,
      await this.fdc3.getOrCreateChannel(constants.ControlChannel)
    );
    return listenerPromise.finally(() => listener.unsubscribe());
  };

  openChannelApp = async (
    testId: string,
    channelId: string,
    commands: string[],
    historyItems?: number,
    notify: boolean = true,
    contextId?: string
  ) => {
    const channelsAppConfig: ChannelsAppConfig = {
      testId: testId,
      channelId: channelId,
      notifyAppAOnCompletion: notify,
      contextId: contextId,
    };

    if (historyItems) {
      channelsAppConfig.historyItems = historyItems;
    }

    //Open ChannelsApp then execute commands in order
    await this.fdc3.open({ appId: 'ChannelsAppId' }, buildChannelsAppContext(commands, channelsAppConfig));
    this.mockAppTracker.markOpened(testId);
  };

  async closeMockAppIfOpened(testId: string) {
    await this.mockAppTracker.closeIfOpened(testId);
  }

  setupAndValidateListener = async (
    channel: Channel | null,
    listenContextType: string | null,
    expectedContextType: string | null,
    errorMessage: string,
    onComplete: (ctx: Context, metadata?: ContextMetadata) => void
  ): Promise<Listener> => {
    let listener;
    if (channel) {
      listener = await channel.addContextListener(listenContextType, (context, metadata) => {
        if (expectedContextType != null) {
          expect(context.type).to.be.equals(expectedContextType, errorMessage);
        }
        onComplete(context, metadata);
      });
    } else {
      listener = await this.fdc3.addContextListener(expectedContextType, (context, metadata) => {
        if (expectedContextType != null) {
          expect(context.type).to.be.equals(expectedContextType, errorMessage);
        }
        onComplete(context, metadata);
      });
    }

    validateListenerObject(listener);
    return listener;
  };

  setupArrayContextListener = async (
    channel: Channel | null,
    listenContextTypes: string[],
    expectedContextTypes: string[],
    errorMessage: string,
    onComplete: (ctx: Context, metadata?: ContextMetadata) => void
  ): Promise<Listener> => {
    let listener;
    if (channel) {
      listener = await channel.addContextListener(listenContextTypes, (context, metadata) => {
        if (expectedContextTypes.length > 0) {
          expect(expectedContextTypes).to.include(context.type, errorMessage);
        }
        onComplete(context, metadata);
      });
    } else {
      listener = await this.fdc3.addContextListener(listenContextTypes, (context, metadata) => {
        if (expectedContextTypes.length > 0) {
          expect(expectedContextTypes).to.include(context.type, errorMessage);
        }
        onComplete(context, metadata);
      });
    }

    validateListenerObject(listener);
    return listener;
  };

  setupContextChecker = async (
    channel: Channel,
    requestedContextType: string | null,
    expectedContextType: string,
    errorMessage: string,
    onComplete: (ctx: Context) => void
  ): Promise<void> => {
    //Retrieve current context from channel
    const context =
      requestedContextType === null
        ? await channel.getCurrentContext()
        : await channel.getCurrentContext(requestedContextType);
    assert.isNotNull(context, 'await channel.getCurrentContext() returned null');
    expect(context?.type, 'retrieved context was not of the expected type').to.be.equals(
      expectedContextType,
      errorMessage
    );
    if (context) {
      onComplete(context);
    }
  };

  setupContextClearedEventListener = async (
    channel: Channel | null,
    errorMessage: string,
    onComplete: (event: FDC3ContextClearedEvent) => void
  ): Promise<Listener> => {
    const handler = (event: ApiEvent) => {
      expect(event.type).to.be.equals('contextCleared', errorMessage);
      onComplete(event as FDC3ContextClearedEvent);
    };

    const listener = channel
      ? await channel.addEventListener('contextCleared', handler)
      : await this.fdc3.addEventListener('contextCleared', handler);

    validateListenerObject(listener);
    return listener;
  };

  getRandomId(): string {
    const uint32 = window.crypto.getRandomValues(new Uint32Array(1))[0];

    return uint32.toString(16);
  }
}

function validateListenerObject(listenerObject: Listener) {
  assert.isTrue(typeof listenerObject === 'object', 'No listener object found');
  expect(typeof listenerObject.unsubscribe).to.be.equals('function', 'Listener does not contain an unsubscribe method');
}

function buildChannelsAppContext(mockAppCommands: string[], config: ChannelsAppConfig): ChannelsAppContext {
  return {
    type: 'channelsAppContext',
    commands: mockAppCommands,
    config: {
      testId: config.testId,
      notifyAppAOnCompletion: config.notifyAppAOnCompletion ?? false,
      historyItems: config.historyItems ?? 1,
      channelId: config.channelId,
      contextId: config.contextId,
    },
  };
}
